import React, { useState, useCallback, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';

import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import { diagnoReparacionSchema, UNIDADES_REPUESTO } from '../../validation/diagno-reparacion';
import obtenerImagen from '../../services/archivosService';

const MAX_FOTOS = 6;
const MAX_MB_POR_FOTO = 5;
const MAX_BYTES_POR_FOTO = MAX_MB_POR_FOTO * 1024 * 1024;

function CampoError({ children }) {
  if (!children) return null;
  return <small className="text-danger d-block mt-1">{children}</small>;
}

/**
 * Shape único para toda foto en el estado, exista o no en el backend
 * @param {*} fotosBackend 
 * @returns 
 * { 
 *  id: string,              // siempre único — clave React y para eliminar
 *  file: File | null,       // solo si es nueva
 *  preview: string | null,  // blob URL, se carga async si es existente
 *  existente: boolean,
 *  id_registro: number | null, // solo si es existente
 * }
 */
function normalizarFotosExistentes(fotosBackend) {
  return fotosBackend
    .sort((a, b) => a.orden - b.orden) // respeta el orden que ya tenían en BD
    .map((f) => ({
      id: `existente-${f.id_registro}`,
      file: null,
      preview: null, // se llena en el useEffect de carga
      existente: true,
      id_registro: f.id_registro,
      ruta: f.ruta, // se guarda para poder pedirla al backend
    }));
}

function DiagnoReparacion({ revisionId, onGuardar, onVolver, valoresIniciales, soloLectura }) {
  const [enviando, setEnviando] = useState(false);
  const [fotos, setFotos] = useState(() =>
    normalizarFotosExistentes(valoresIniciales?.fotografias ?? [])
  );
  const [errorFotos, setErrorFotos] = useState(null);

  const { 
    control, handleSubmit, formState: { errors },
  } = useForm({
    resolver: yupResolver(diagnoReparacionSchema),
    mode: 'onTouched',
    defaultValues: {
      diagnostico: valoresIniciales?.trabajoTaller?.diagnostico ?? '',
      recomendado: valoresIniciales?.trabajoTaller?.recomendado ?? '',
      realizado: valoresIniciales?.trabajoTaller?.realizado ?? '',
      obs_mecanico: valoresIniciales?.trabajoTaller?.obs_mecanico ?? '',
      repuestos: valoresIniciales?.repuestos ?? [],
    }
  });

  const { 
    fields, append, remove 
  } = useFieldArray({
    control,
    name: 'repuestos',
  });

  useEffect(() => {
    let cancelado = false;
    const blobsCreados = [];

    async function cargarPreviews() {
      const fotosSinPreview = fotos.filter((f) => f.existente && !f.preview);
      if (fotosSinPreview.length === 0) return;

      const actualizaciones = await Promise.all(
        fotosSinPreview.map(async (foto) => {
          try {
            const blobUrl = await obtenerImagen(foto.ruta);
            blobsCreados.push(blobUrl);
            return { id: foto.id, preview: blobUrl };
          } catch (err) {
            console.error(`No se pudo cargar la foto ${foto.id}:`, err);
            return { id: foto.id, preview: null, error: true };
          }
        })
      );

      if (cancelado) return;

      setFotos((prev) =>
        prev.map((f) => {
          const actualizacion = actualizaciones.find((a) => a.id === f.id);
          return actualizacion ? { ...f, ...actualizacion } : f;
        })
      );
    }

    cargarPreviews();

    return () => {
      cancelado = true;
      blobsCreados.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // solo al montar — las fotos nuevas ya traen su preview al agregarse

  // ---------------- Fotografías ----------------
  const manejarSeleccionFotos = useCallback(
    (e) => {
      const archivos = Array.from(e.target.files || []);
      e.target.value = ''; // permite volver a elegir el mismo archivo si lo saca y lo agrega de nuevo

      if (archivos.length === 0) return;

      setErrorFotos(null);

      setFotos((prev) => {
        const disponibles = MAX_FOTOS - prev.length;

        if (disponibles <= 0) {
          setErrorFotos(`Ya alcanzaste el máximo de ${MAX_FOTOS} fotografías.`);
          return prev;
        }

        const validas = [];
        const rechazadasPorTipo = [];
        const rechazadasPorPeso = [];

        archivos.forEach((file) => {
          if (!file.type.startsWith('image/')) {
            rechazadasPorTipo.push(file.name);
            return;
          }
          if (file.size > MAX_BYTES_POR_FOTO) {
            rechazadasPorPeso.push(file.name);
            return;
          }
          validas.push(file);
        });

        const aAgregar = validas.slice(0, disponibles);
        const excedentes = validas.length > disponibles;

        const nuevas = aAgregar.map((file) => ({
          id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          preview: URL.createObjectURL(file),
          existente: false,
          id_registro: null,
        }));

        const mensajes = [];
        if (rechazadasPorTipo.length) mensajes.push(`No son imágenes: ${rechazadasPorTipo.join(', ')}`);
        if (rechazadasPorPeso.length) mensajes.push(`Superan ${MAX_MB_POR_FOTO}MB: ${rechazadasPorPeso.join(', ')}`);
        if (excedentes) mensajes.push(`Solo se agregaron ${disponibles} (máximo ${MAX_FOTOS} en total).`);
        if (mensajes.length) setErrorFotos(mensajes.join(' — '));

        return [...prev, ...nuevas];
      });
    },
    []
  );

  const quitarFoto = (id) => {
    setFotos((prev) => {
      const foto = prev.find((f) => f.id === id);
      if (foto?.preview && !foto.existente) {
        URL.revokeObjectURL(foto.preview); // solo revoca blobs de File locales
      }
      return prev.filter((f) => f.id !== id);
    });
    setErrorFotos(null);
  };
  
  // ---------------- Submit ----------------
  const onSubmit = async (data) => {
    setEnviando(true);
    try {
      const payload = {
        ...data,
        fotografias: fotos,
        tallerCompleto: true,
      }
      await onGuardar?.(payload);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="tmc-form mt-3 mb-6">
      <form 
        className="container-form" 
        onSubmit={handleSubmit(onSubmit)} 
        noValidate
      >
        {/* ---------------- Trabajo recomendado / realizado ---------------- */}
        <div className="card-nc">
          <h5 className="mb-3">I. Trabajo en taller</h5>

          <div className="mb-2 form-comprobante-field">
            <label className="form-label fw-bold">Diagnóstico</label>
            <Controller
              name="diagnostico"
              control={control}
              render={({ field }) => (
                <>
                  <InputTextarea
                    {...field}
                    value={field.value ?? ''}
                    rows={4}
                    autoResize
                    className="w-100"
                    maxLength={500}
                    style={{ minHeight: '100px' }}
                    placeholder="Descripción  de las fallas o problemas detectados..."
                  />
                  <small className="d-block text-end text-muted">
                    {(field.value ?? "").length}/500
                  </small>
                </>
              )}
            />
          </div>

          <div className="mb-2 form-comprobante-field">
            <label className="form-label fw-bold">Trabajo recomendado</label>
            <Controller
              name="recomendado"
              control={control}
              render={({ field }) => (
                <>
                  <InputTextarea
                    {...field}
                    value={field.value ?? ''}
                    rows={4}
                    autoResize
                    className="w-100"
                    maxLength={500}
                    style={{ minHeight: '100px' }}
                    placeholder="Trabajo que se recomienda realizar a futuro..."
                  />
                  <small className="d-block text-end text-muted">
                    {(field.value ?? "").length}/500
                  </small>
                </>
              )}
            />
          </div>

          <div className="mb-2 form-comprobante-field">
            <label className="form-label fw-bold">Trabajo realizado</label>
            <Controller
              name="realizado"
              control={control}
              render={({ field }) => (
                <>
                  <InputTextarea
                    {...field}
                    value={field.value ?? ''}
                    rows={4}
                    autoResize
                    className="w-100"
                    maxLength={500}
                    style={{ minHeight: '100px' }}
                    placeholder="Trabajo efectivamente realizado en esta visita..."
                  />
                  <small className="d-block text-end text-muted">
                    {(field.value ?? "").length}/500
                  </small>
                </>
              )}
            />
          </div>

          <div className="mb-2 form-comprobante-field">
            <label className="form-label fw-bold">Observación del mecánico</label>
            <Controller
              name="obs_mecanico"
              control={control}
              render={({ field }) => (
                <>
                  <InputTextarea
                    {...field}
                    value={field.value ?? ''}
                    rows={3}
                    autoResize
                    className="w-100"
                    maxLength={500}
                    style={{ minHeight: '80px' }}
                    placeholder="Observaciones adicionales..."
                  />
                  <small className="d-block text-end text-muted">
                    {(field.value ?? "").length}/500
                  </small>
                </>
              )}
            />
          </div>
        </div>

        {/* ---------------- Repuestos ---------------- */}
        <div className="card-nc">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">II. Repuestos utilizados</h5>
            <Button
              type="button"
              label="Agregar repuesto"
              icon="pi pi-plus"
              className="btn-lleno-azul"
              onClick={() => append({ nombre: '', cantidad: 1, unidad: 'un' })}
            />
          </div>

          {fields.length === 0 && (
            <p className="text-muted mb-0">No se han agregado repuestos todavía.</p>
          )}

          {fields.map((item, index) => (
            <div key={item.id} className="form-comprobante-row align-items-start mb-2">
              <div className="mb-2 form-comprobante-field" style={{ flex: 2 }}>
                {index === 0 && <label className="form-label">Nombre</label>}
                <Controller
                  name={`repuestos.${index}.nombre`}
                  control={control}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      value={field.value ?? ''}
                      className={classNames('w-100', {
                        'p-invalid': errors.repuestos?.[index]?.nombre,
                      })}
                      maxLength={100}
                      placeholder="Ej: Filtro de aceite"
                    />
                  )}
                />
                <CampoError>{errors.repuestos?.[index]?.nombre?.message}</CampoError>
              </div>

              <div className="mb-2 form-comprobante-field" style={{ flex: 1 }}>
                {index === 0 && <label className="form-label">Cantidad</label>}
                <Controller
                  name={`repuestos.${index}.cantidad`}
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value ?? null}
                      onValueChange={(e) => field.onChange(e.value)}
                      className={classNames('w-100', {
                        'p-invalid': errors.repuestos?.[index]?.cantidad,
                      })}
                      inputClassName="w-100"
                      min={0}
                      minFractionDigits={0}
                      maxFractionDigits={2}
                      maxLength={10}
                    />
                  )}
                />
                <CampoError>{errors.repuestos?.[index]?.cantidad?.message}</CampoError>
              </div>

              <div className="mb-2 form-comprobante-field" style={{ flex: 1 }}>
                {index === 0 && <label className="form-label">Unidad</label>}
                <Controller
                  name={`repuestos.${index}.unidad`}
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={UNIDADES_REPUESTO}
                      optionLabel="label"
                      optionValue="value"
                      className={classNames('w-100', {
                        'p-invalid': errors.repuestos?.[index]?.unidad,
                      })}
                      onChange={(e) => field.onChange(e.value)}
                    />
                  )}
                />
                <CampoError>{errors.repuestos?.[index]?.unidad?.message}</CampoError>
              </div>

              <div className="mb-2 d-flex align-items-center" style={{ paddingTop: index === 0 ? 32 : 0 }}>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  className="btn-outline-rojo"
                  onClick={() => remove(index)}
                  aria-label="Quitar repuesto"
                />
              </div>
            </div>
          ))}
        </div>

        {/* ---------------- Fotografías ---------------- */}
        <div className="card-nc">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">III. Fotografías</h5>
            <label className="btn-outline-azul"> {/* className="btn btn-outline-primary btn-sm mb-0" */}
              <i className="pi pi-camera me-2" />
              Agregar fotos
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={manejarSeleccionFotos}
                disabled={fotos.length >= MAX_FOTOS}
              />
            </label>
          </div>

          <small className="text-muted d-block mb-2">
            Máximo {MAX_FOTOS} fotos, {MAX_MB_POR_FOTO}MB cada una. Llevas {fotos.length}/{MAX_FOTOS}.
          </small>

          <CampoError>{errorFotos}</CampoError>

          {fotos.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {fotos.map((foto) => (
                <div
                  key={foto.id}
                  style={{
                    position: 'relative',
                    width: 100,
                    height: 100,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid #ddd',
                  }}
                >
                  <img
                    src={foto.preview}
                    alt="Foto del vehículo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <button
                    type="button"
                    onClick={() => quitarFoto(foto.id)}
                    aria-label="Quitar foto"
                    style={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: 22,
                      height: 22,
                      lineHeight: '22px',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---------------- Acciones ---------------- */}
        <div className="contenedor-botones">
          <Button
            type="button"
            label="Paso anterior"
            icon="pi pi-arrow-left"
            className="btn-raised-azul"
            onClick={onVolver}
            disabled={enviando}
          />
          <Button
            type="submit"
            label="Guardar"
            icon="pi pi-check-circle"
            className='btn-lleno-verde'
            loading={enviando}
            disabled={enviando || !revisionId}
          />
        </div>
      </form>
    </section>
  );
}

export default DiagnoReparacion;